// Imprimir la etiqueta propia de un bulto (el que no viene de Mercado Libre).
//
// El SVG no se abre en una pestaña nueva apuntando al endpoint: esa URL pide
// el token en la cabecera y el navegador no lo manda solo. Se trae por fetch y
// se imprime desde acá.
//
// Dos puertas para el mismo código: el vendedor imprime la etiqueta de su
// envío (`deliveries`), y el depósito la reimprime cuando el bulto llegó sin
// nada pegado (`warehouse`, que va por la capacidad warehouse_access).

import { authFetch } from "./authFetch";

export type PackageLabel = {
  delivery_id: number;
  code: string;
  qr_payload: string;
  receiver: string | null;
  postal_code: string | null;
  svg: string;
};

export async function fetchPackageLabel(
  deliveryId: number | string,
  { token, scope = "deliveries" }: { token: string | null; scope?: "deliveries" | "warehouse" }
): Promise<PackageLabel> {
  const path =
    scope === "warehouse"
      ? `/v2/warehouse/deliveries/${deliveryId}/label`
      : `/v2/deliveries/${deliveryId}/label`;

  const response = await authFetch(path, { token, method: "POST" });
  const json = await response.json().catch(() => ({} as any));
  if (!response.ok || json?.ok === false) {
    throw new Error(json?.error || json?.message || `Error ${response.status}`);
  }
  return json.label as PackageLabel;
}

export async function printPackageLabel(
  deliveryId: number | string,
  opts: { token: string | null; scope?: "deliveries" | "warehouse" }
): Promise<PackageLabel> {
  const label = await fetchPackageLabel(deliveryId, opts);

  const win = window.open("", "_blank", "width=700,height=560");
  if (!win) throw new Error("El navegador bloqueó la ventana de impresión. Permitila y probá de nuevo.");

  // La etiqueta mide 600x380 puntos; se imprime centrada y sin márgenes del
  // navegador para que salga del mismo tamaño en cualquier impresora.
  win.document.write(
    `<!doctype html><html lang="es"><head><meta charset="utf-8">` +
      `<title>Etiqueta ${label.code}</title>` +
      `<style>@page { margin: 8mm } body { margin: 0; display: flex; justify-content: center; align-items: flex-start; font-family: system-ui, sans-serif }</style>` +
      `</head><body>${label.svg}</body></html>`
  );
  win.document.close();
  win.focus();
  // El print va después del load: si se dispara antes, algunas impresoras
  // sacan la hoja en blanco porque el QR todavía no se dibujó.
  // Una sola vez: el evento de carga y el temporizador de respaldo llegaban
  // los dos y abrían dos diálogos de impresión (visto el 15-sep-2026).
  let printed = false;
  const printOnce = () => {
    if (printed) return;
    printed = true;
    try { win.print(); } catch { /* el operador imprime a mano */ }
  };
  win.addEventListener("load", printOnce);
  window.setTimeout(printOnce, 500);

  return label;
}
